import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

const NAME_STORAGE_KEY = "tradeNoteAuthor";

function formatNoteTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const TradeNotesModal = ({ trade, onClose, onSaved }) => {
  const [notes, setNotes] = useState(trade?.notes || []);
  const [draft, setDraft] = useState("");
  const [author, setAuthor] = useState(
    () => localStorage.getItem(NAME_STORAGE_KEY) || "",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const noteText = draft.trim();
  const authorText = author.trim();
  const canSave = noteText.length > 0 && authorText.length > 0;

  useEffect(() => {
    if (!trade?._id) return;
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(`/trades/${trade._id}/notes`)
      .then((res) => {
        if (!cancelled) setNotes(res.data?.notes || []);
      })
      .catch(() => {
        if (!cancelled) setNotes(trade?.notes || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trade?._id]);

  const handleClose = async () => {
    if (saving) return;
    if (!noteText) {
      onClose();
      return;
    }
    if (!authorText) {
      toast.error("Enter your name to save the note.");
      return;
    }
    try {
      setSaving(true);
      localStorage.setItem(NAME_STORAGE_KEY, authorText);
      const res = await axiosInstance.post(`/trades/${trade._id}/notes`, {
        text: noteText,
        author: authorText,
      });
      const savedNotes = res.data?.notes || [];
      onSaved?.(savedNotes);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save note.");
      setSaving(false);
    }
  };

  let status = "Type a note first";
  if (noteText && !authorText) status = "Enter your name to save";
  else if (canSave) status = "Will save when you click Close";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg relative">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            Trade Notes #{trade?.tradeNumber}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-2xl text-gray-500 hover:text-gray-700 leading-none"
            aria-label="Close notes"
          >
            &times;
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-sm text-gray-500">Loading notes...</div>
          ) : notes.length > 0 ? (
            <div className="space-y-2">
              {[...notes]
                .slice()
                .reverse()
                .map((note, idx) => (
                  <div
                    key={note._id || `${note.createdAt}-${idx}`}
                    className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2"
                  >
                    <div className="text-xs text-gray-600 mb-1">
                      <span className="font-medium text-gray-800">
                        {note.author || "Unknown"}
                      </span>
                      {" · "}
                      {formatNoteTime(note.createdAt)}
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">
                      {note.text}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-sm text-gray-400">No notes yet.</div>
          )}

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">
              Note
            </label>
            <textarea
              className="w-full border rounded-md p-2 text-sm min-h-[110px] focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Type your note..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">
              Your name
            </label>
            <input
              type="text"
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Enter your name to save..."
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              disabled={!noteText || saving}
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t">
          <span className="text-sm text-gray-400">{status}</span>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving..." : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeNotesModal;
